import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PawPrint, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing or invalid reset link");
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      setDone(true);
      // Backend now auto-signs the user in via cookie. Pull the fresh profile and
      // route straight to the right surface so the volunteer never sees the login
      // screen again — fixes the iPhone / in-app-browser cookie-drop handoff.
      try { if (refreshUser) await refreshUser(); } catch (err) { /* non-fatal */ }
      const dest = data?.role === "admin" ? "/admin" : "/app";
      setTimeout(() => navigate(dest, { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <PawPrint className="w-7 h-7 text-fern" strokeWidth={1.5} />
          <span className="font-heading text-xl font-semibold">Satchel&apos;s Last Resort</span>
        </div>
        {done ? (
          <div className="tactile-card p-8 text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-fern mx-auto" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-semibold">Password updated</h1>
            <p className="text-muted-foreground">Welcome in — taking you to your dashboard…</p>
          </div>
        ) : (
          <>
            <p className="label-caps mb-2">New volunteer? Just set a password</p>
            <h1 className="font-heading text-4xl font-bold tracking-tight mb-6">Set your password.</h1>
            <form onSubmit={submit} className="space-y-4 tactile-card p-6" data-testid="reset-form">
              <div>
                <Label htmlFor="pw">New password (min 6 characters)</Label>
                <Input id="pw" type="password" required minLength={6}
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password" className="rounded-xl h-12" />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm password</Label>
                <Input id="pw2" type="password" required minLength={6}
                  value={pw2} onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-password-confirm" className="rounded-xl h-12" />
              </div>
              <Button type="submit" disabled={!token || busy} data-testid="reset-submit"
                className="w-full h-12 rounded-full bg-fern hover:bg-fern-dark text-white font-medium">
                {busy ? "Saving…" : "Set password"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Wrong link? <Link to="/login" className="text-fern underline">Back to sign in</Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
